import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../hooks/useAuth";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const TIER_COLORS = {
  free:       { bg: "#1a1f2e", accent: "#4a5568",  label: "FREE" },
  basic:      { bg: "#0d1f2d", accent: "#3b82f6",  label: "BASIC" },
  pro:        { bg: "#0d1a0d", accent: "#22c55e",  label: "PRO" },
  enterprise: { bg: "#1a0d1a", accent: "#a855f7",  label: "ENTERPRISE" },
};

const TIER_LIMITS = {
  free:       { regions: 1,           history: "7 days",    api_calls: "None",           ai: false },
  basic:      { regions: 3,           history: "30 days",   api_calls: "1,000/day",      ai: false },
  pro:        { regions: "Unlimited", history: "365 days",  api_calls: "10,000/day",     ai: true  },
  enterprise: { regions: "Unlimited", history: "Unlimited", api_calls: "Unlimited",      ai: true  },
};

export default function Account() {
  const { user, logout } = useAuth();
  const [sub, setSub] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [creatingKey, setCreatingKey] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [showNewKey, setShowNewKey] = useState(null);
  const [error, setError] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const getToken = () => localStorage.getItem("hh_token");

  // Fetch fresh subscription from backend (not from stale JWT)
  const fetchSubscription = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/my-subscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setSub(data);
    } catch (e) {
      console.error("Failed to fetch subscription", e);
    }
  }, []);

  const fetchApiKeys = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setLoadingKeys(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setApiKeys(Array.isArray(data.api_keys) ? data.api_keys : []);
    } catch (e) {
      setError("Failed to load API keys");
    } finally {
      setLoadingKeys(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Check for upgrade=success in URL and refetch subscription
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("upgrade") === "success") {
      // Poll a few times to wait for webhook
      let attempts = 0;
      const poll = setInterval(() => {
        fetchSubscription();
        attempts++;
        if (attempts >= 5) clearInterval(poll);
      }, 2000);
      window.history.replaceState({}, "", "/account");
    }
  }, [fetchSubscription]);

  const tier = sub?.tier || user?.tier || "free";

  useEffect(() => {
    if (tier !== "free") fetchApiKeys();
  }, [tier, fetchApiKeys]);

  const tierConfig = TIER_COLORS[tier] || TIER_COLORS.free;
  const tierLimits = TIER_LIMITS[tier] || TIER_LIMITS.free;

  const createApiKey = async () => {
    if (!newKeyName.trim()) return;
    const token = getToken();
    setCreatingKey(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/api-keys?name=${encodeURIComponent(newKeyName)}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setShowNewKey(data.key);
      setNewKeyName("");
      fetchApiKeys();
    } catch (e) {
      setError("Failed to create API key");
    } finally {
      setCreatingKey(false);
    }
  };

  const revokeApiKey = async (keyId) => {
    const token = getToken();
    try {
      await fetch(`${API_URL}/api/auth/api-keys/${keyId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchApiKeys();
    } catch (e) {
      setError("Failed to revoke API key");
    }
  };

  const handleUpgrade = async (targetTier) => {
    setUpgrading(true);
    const token = getToken();
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/checkout/${targetTier}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const data = await res.json();
if (data.checkout_url) {
  window.location.href = data.checkout_url;
} else if (data.message) {
  window.location.href = "mailto:sales@nightingaleint.io?subject=Enterprise Enquiry — High Horse Intel";
}
    } catch (e) {
      setError("Failed to start checkout");
    } finally {
      setUpgrading(false);
    }
  };

  const handleCancel = async () => {
  if (!window.confirm("Are you sure you want to cancel your subscription?")) return;
  const token = getToken();
  setCancelling(true);
  try {
    const res = await fetch(`${API_URL}/api/subscriptions/cancel`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.detail || "Failed to cancel");
    }
    await fetchSubscription();
    alert("Your subscription will cancel at the end of your billing period.");
  } catch (e) {
    setError(e.message || "Failed to cancel subscription");
  } finally {
    setCancelling(false);
  }
};

  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  if (!user) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>Please log in to view your account.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={styles.headerLabel}>ACCOUNT</div>
          <h1 style={styles.headerTitle}>Your Profile</h1>
          <p style={styles.headerSub}>Manage your subscription, API keys and settings</p>
        </div>
        <button onClick={logout} style={styles.logoutBtn}>SIGN OUT</button>
      </div>

      {error && (
        <div style={styles.errorBanner}>
          {error}
          <button onClick={() => setError(null)} style={styles.errorClose}>✕</button>
        </div>
      )}

      {/* Profile + Subscription */}
      <div style={styles.grid2}>
        {/* Profile Card */}
        <div style={styles.card}>
          <div style={styles.cardLabel}>PROFILE</div>
          <div style={styles.profileRow}>
            <div style={styles.avatar}>{user.email?.[0]?.toUpperCase() || "U"}</div>
            <div>
              <div style={styles.profileEmail}>{user.email}</div>
              <div style={styles.profileMeta}>
                Member since {user.created_at
                  ? new Date(user.created_at).toLocaleDateString("en-GB", { month: "long", year: "numeric" })
                  : "—"}
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Card */}
        <div style={{ ...styles.card, borderColor: tierConfig.accent + "40", background: tierConfig.bg }}>
          <div style={styles.cardLabel}>SUBSCRIPTION</div>
          <div style={styles.tierBadge(tierConfig.accent)}>{tierConfig.label}</div>
          <div style={styles.tierLimits}>
            {[
              ["Regions",     tierLimits.regions],
              ["History",     tierLimits.history],
              ["API Calls",   tierLimits.api_calls],
              ["AI Features", tierLimits.ai ? "✓ Included" : "✗ Not included"],
            ].map(([k, v]) => (
              <div key={k} style={styles.limitRow}>
                <span style={styles.limitKey}>{k}</span>
                <span style={styles.limitVal}>{v}</span>
              </div>
            ))}
          </div>
          <div style={styles.tierActions}>
            {tier === "free" && (
  <>
    <button onClick={() => handleUpgrade("basic")} style={styles.upgradeBtn("#3b82f6")} disabled={upgrading}>
      {upgrading ? "..." : "UPGRADE TO BASIC — $49/mo"}
    </button>
    <button onClick={() => window.location.href = "mailto:sales@highhorse.io?subject=Enterprise Enquiry — High Horse Intel"} style={styles.upgradeBtn("#a855f7")}>
      CONTACT US FOR ENTERPRISE
    </button>
  </>
)}
            {tier === "basic" && (
              <>
                <button onClick={() => handleUpgrade("pro")} style={styles.upgradeBtn("#22c55e")} disabled={upgrading}>
                  {upgrading ? "..." : "UPGRADE TO PRO — $149/mo"}
                </button>
                <button onClick={handleCancel} style={styles.cancelBtn} disabled={cancelling}>
                  {cancelling ? "..." : "Cancel subscription"}
                </button>
              </>
            )}
            {tier === "pro" && (
              <>
                <button onClick={() => handleUpgrade("enterprise")} style={styles.upgradeBtn("#a855f7")} disabled={upgrading}>
                  {upgrading ? "..." : "UPGRADE TO ENTERPRISE"}
                </button>
                <button onClick={handleCancel} style={styles.cancelBtn} disabled={cancelling}>
                  {cancelling ? "..." : "Cancel subscription"}
                </button>
              </>
            )}
            {tier === "enterprise" && (
              <button onClick={handleCancel} style={styles.cancelBtn} disabled={cancelling}>
                {cancelling ? "..." : "Cancel subscription"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* API Keys */}
      {tier !== "free" && (
        <div style={styles.card}>
          <div style={styles.cardLabel}>API KEYS</div>
          <p style={styles.apiKeyInfo}>
            Use these keys to access the High Horse Intel API programmatically.
            Keep them secret — do not share or expose in client-side code.
          </p>

          {showNewKey && (
            <div style={styles.newKeyBanner}>
              <div style={styles.newKeyLabel}>⚠ Copy this key now — it won't be shown again</div>
              <div style={styles.newKeyRow}>
                <code style={styles.keyCode}>{showNewKey}</code>
                <button onClick={() => copyToClipboard(showNewKey)} style={styles.copyBtn}>COPY</button>
                <button onClick={() => setShowNewKey(null)} style={styles.dismissBtn}>DISMISS</button>
              </div>
            </div>
          )}

          <div style={styles.createKeyRow}>
            <input
              value={newKeyName}
              onChange={e => setNewKeyName(e.target.value)}
              placeholder="Key name (e.g. production, testing)"
              style={styles.input}
              onKeyDown={e => e.key === "Enter" && createApiKey()}
            />
            <button onClick={createApiKey} style={styles.createBtn} disabled={creatingKey || !newKeyName.trim()}>
              {creatingKey ? "CREATING..." : "+ CREATE KEY"}
            </button>
          </div>

          {loadingKeys ? (
            <div style={styles.loadingText}>Loading keys...</div>
          ) : apiKeys.length === 0 ? (
            <div style={styles.emptyKeys}>No API keys yet. Create one above.</div>
          ) : (
            <div style={styles.keysList}>
              {apiKeys.map(k => (
                <div key={k.id} style={styles.keyRow}>
                  <div style={styles.keyInfo}>
                    <div style={styles.keyName}>{k.name}</div>
                    <div style={styles.keyMeta}>
                      {k.last_used
                        ? `Last used ${new Date(k.last_used).toLocaleDateString()}`
                        : "Never used"}
                    </div>
                  </div>
                  <code style={styles.keyPreview}>{k.key?.slice(0, 12)}••••••••</code>
                  <button onClick={() => copyToClipboard(k.key)} style={styles.copyBtn}>COPY</button>
                  <button onClick={() => revokeApiKey(k.id)} style={styles.revokeBtn}>REVOKE</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upgrade CTA for free users */}
      {tier === "free" && (
        <div style={styles.ctaCard}>
          <div style={styles.ctaTitle}>Unlock the full platform</div>
          <div style={styles.ctaGrid}>
            {[
              {
                id: "basic", color: "#3b82f6", name: "BASIC", price: "$49",
                features: ["3 regions", "30-day history", "API access (1,000 calls/day)"],
              },
              {
                id: "pro", color: "#22c55e", name: "PRO", price: "$149",
                features: ["All regions", "365-day history", "AI features included", "10,000 API calls/day"],
              },
              {
                id: "enterprise", color: "#a855f7", name: "ENTERPRISE", price: "Custom",
                features: ["Unlimited everything", "White-label", "Dedicated support", "Custom integrations"],
              },
            ].map(plan => (
              <div key={plan.id} style={styles.ctaPlan}>
                <div style={styles.ctaPlanName(plan.color)}>{plan.name}</div>
                <div style={styles.ctaPrice}>
                  {plan.price}
                  {plan.price !== "Custom" && <span style={styles.ctaPer}>/mo</span>}
                </div>
                <ul style={styles.ctaFeatures}>
                  {plan.features.map(f => <li key={f}>{f}</li>)}
                </ul>
                <button
                  onClick={() => plan.id === "enterprise"
  ? window.location.href = "mailto:sales@highhorse.io?subject=Enterprise Enquiry — High Horse Intel"
  : handleUpgrade(plan.id)
}
                  style={styles.upgradeBtn(plan.color)}
                  disabled={upgrading}
                >
                  {upgrading ? "..." : plan.id === "enterprise" ? "CONTACT US" : "GET STARTED"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: "32px", maxWidth: "1100px", margin: "0 auto", fontFamily: "'DM Mono', monospace", color: "#e2e8f0" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "32px" },
  headerLabel: { fontSize: "10px", letterSpacing: "3px", color: "#4a5568", marginBottom: "8px" },
  headerTitle: { fontSize: "28px", fontFamily: "'Syne', sans-serif", fontWeight: 800, margin: "0 0 8px 0", color: "#f1f5f9" },
  headerSub: { fontSize: "13px", color: "#64748b", margin: 0 },
  logoutBtn: { background: "transparent", border: "1px solid #2d3748", color: "#64748b", padding: "8px 16px", fontSize: "11px", letterSpacing: "2px", cursor: "pointer", fontFamily: "'DM Mono', monospace" },
  errorBanner: { background: "#2d1515", border: "1px solid #7f1d1d", color: "#fca5a5", padding: "12px 16px", marginBottom: "24px", fontSize: "13px", display: "flex", justifyContent: "space-between", alignItems: "center" },
  errorClose: { background: "transparent", border: "none", color: "#fca5a5", cursor: "pointer", fontSize: "16px" },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "16px" },
  card: { background: "#0d1117", border: "1px solid #1e2738", padding: "24px", marginBottom: "16px" },
  cardLabel: { fontSize: "10px", letterSpacing: "3px", color: "#4a5568", marginBottom: "16px" },
  profileRow: { display: "flex", alignItems: "center", gap: "16px" },
  avatar: { width: "48px", height: "48px", background: "#1e2738", border: "1px solid #2d3748", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", fontWeight: 700, color: "#94a3b8", flexShrink: 0 },
  profileEmail: { fontSize: "14px", color: "#e2e8f0", marginBottom: "4px" },
  profileMeta: { fontSize: "12px", color: "#4a5568" },
  tierBadge: (accent) => ({ display: "inline-block", background: accent + "20", border: `1px solid ${accent}60`, color: accent, padding: "4px 12px", fontSize: "11px", letterSpacing: "3px", marginBottom: "16px" }),
  tierLimits: { marginBottom: "16px" },
  limitRow: { display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #1e2738", fontSize: "12px" },
  limitKey: { color: "#4a5568" },
  limitVal: { color: "#94a3b8" },
  tierActions: { display: "flex", flexDirection: "column", gap: "8px", marginTop: "16px" },
  upgradeBtn: (color) => ({ background: color + "15", border: `1px solid ${color}60`, color: color, padding: "10px 16px", fontSize: "11px", letterSpacing: "2px", cursor: "pointer", fontFamily: "'DM Mono', monospace", width: "100%" }),
  cancelBtn: { background: "transparent", border: "none", color: "#4a5568", fontSize: "11px", cursor: "pointer", textDecoration: "underline", fontFamily: "'DM Mono', monospace", padding: "4px 0" },
  apiKeyInfo: { fontSize: "12px", color: "#4a5568", marginBottom: "20px", lineHeight: "1.6" },
  newKeyBanner: { background: "#1a1500", border: "1px solid #854d0e", padding: "16px", marginBottom: "20px" },
  newKeyLabel: { fontSize: "11px", color: "#ca8a04", marginBottom: "10px", letterSpacing: "1px" },
  newKeyRow: { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" },
  keyCode: { flex: 1, background: "#0d1117", border: "1px solid #2d3748", padding: "8px 12px", fontSize: "11px", color: "#22c55e", wordBreak: "break-all" },
  copyBtn: { background: "#1e2738", border: "1px solid #2d3748", color: "#94a3b8", padding: "8px 12px", fontSize: "10px", letterSpacing: "2px", cursor: "pointer", fontFamily: "'DM Mono', monospace", flexShrink: 0 },
  dismissBtn: { background: "transparent", border: "1px solid #2d3748", color: "#4a5568", padding: "8px 12px", fontSize: "10px", letterSpacing: "2px", cursor: "pointer", fontFamily: "'DM Mono', monospace", flexShrink: 0 },
  createKeyRow: { display: "flex", gap: "8px", marginBottom: "20px" },
  input: { flex: 1, background: "#060910", border: "1px solid #1e2738", color: "#e2e8f0", padding: "10px 14px", fontSize: "12px", fontFamily: "'DM Mono', monospace", outline: "none" },
  createBtn: { background: "#1e2738", border: "1px solid #2d3748", color: "#94a3b8", padding: "10px 16px", fontSize: "11px", letterSpacing: "2px", cursor: "pointer", fontFamily: "'DM Mono', monospace", flexShrink: 0 },
  loadingText: { fontSize: "12px", color: "#4a5568", padding: "16px 0" },
  emptyKeys: { fontSize: "12px", color: "#4a5568", padding: "16px 0", textAlign: "center" },
  keysList: { display: "flex", flexDirection: "column", gap: "8px" },
  keyRow: { display: "flex", alignItems: "center", gap: "12px", padding: "12px", background: "#060910", border: "1px solid #1e2738" },
  keyInfo: { flex: 1 },
  keyName: { fontSize: "13px", color: "#e2e8f0", marginBottom: "2px" },
  keyMeta: { fontSize: "11px", color: "#4a5568" },
  keyPreview: { fontSize: "11px", color: "#4a5568", background: "#0d1117", padding: "4px 8px", flexShrink: 0 },
  revokeBtn: { background: "transparent", border: "1px solid #7f1d1d", color: "#ef4444", padding: "6px 10px", fontSize: "10px", letterSpacing: "2px", cursor: "pointer", fontFamily: "'DM Mono', monospace", flexShrink: 0 },
  ctaCard: { background: "#0d1117", border: "1px solid #1e2738", padding: "32px", marginTop: "16px" },
  ctaTitle: { fontFamily: "'Syne', sans-serif", fontSize: "22px", fontWeight: 800, color: "#f1f5f9", marginBottom: "24px" },
  ctaGrid: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" },
  ctaPlan: { background: "#060910", border: "1px solid #1e2738", padding: "24px" },
  ctaPlanName: (color) => ({ fontSize: "11px", letterSpacing: "3px", color: color, marginBottom: "8px" }),
  ctaPrice: { fontFamily: "'Syne', sans-serif", fontSize: "36px", fontWeight: 800, color: "#f1f5f9", marginBottom: "16px" },
  ctaPer: { fontSize: "16px", color: "#4a5568" },
  ctaFeatures: { listStyle: "none", padding: 0, margin: "0 0 20px 0", fontSize: "12px", color: "#64748b", lineHeight: "2" },
  emptyState: { textAlign: "center", padding: "80px 0" },
  emptyText: { color: "#4a5568", fontSize: "14px" },
};
